import React, { useRef, useState } from 'react';
import { View, Text, TouchableOpacity, ActivityIndicator, StyleSheet, Platform, Modal } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useRTLStyles } from '../hooks/useRTLStyles';
import { useRightPaneWidth } from '../hooks/useRightPaneWidth';
import { colors, spacing, typography } from '../theme';
import { designTokens } from '../theme/design-system';
import { useAppStore } from '../store/appStore';
import { PANE_ANIMATION_DURATION } from '../constants/paneLayout';
import { useScreenHeaderHeight } from '../contexts/ScreenHeaderHeightContext';
import { useThemedStyles } from '../hooks/useThemedStyles';
import { useHardwareBackAction } from '../navigation/hardwareBack';
import { useLocale } from '../contexts/LocaleContext';
import type { TranslationKey } from '../i18n';

const warnedUnlabeled = new Set<string>();

/** Last-resort titles by icon, so a menu row is never an unnamed icon. */
const ICON_TITLES: Partial<Record<string, TranslationKey>> = {
  'add': 'common.add', 'add-circle-outline': 'common.add', 'create-outline': 'common.edit', 'pencil': 'common.edit', 'pencil-outline': 'common.edit',
  'trash-outline': 'common.delete', 'trash': 'common.delete', 'copy-outline': 'common.copy', 'filter': 'common.filter', 'filter-outline': 'common.filter',
  'search': 'common.search', 'search-outline': 'common.search', 'close': 'common.close', 'checkmark': 'common.save',
  'download-outline': 'common.download', 'cloud-download-outline': 'common.download', 'cloud-upload-outline': 'common.import', 'share-outline': 'common.share',
  'share-social-outline': 'common.share', 'print-outline': 'common.print', 'refresh': 'common.refresh', 'refresh-outline': 'common.refresh',
  'checkbox-outline': 'common.select', 'settings-outline': 'common.settings', 'options-outline': 'common.options', 'eye-outline': 'common.view',
  'swap-vertical': 'common.sort', 'swap-vertical-outline': 'common.sort', 'send': 'common.send', 'send-outline': 'common.send',
};

export interface FormHeaderAction {
  icon: keyof typeof Ionicons.glyphMap;
  onPress: () => void;
  /** Required for actions that may land in the overflow menu (anything past
   * the first two non-primary actions) — shown as that row's label there.
   * Optional for actions guaranteed to stay visible (primary, or apps with
   * <=2 total actions), but safest to always pass one. */
  label?: string;
  /** Renders as the filled circular primary button (save/submit/add) instead
   * of a plain icon button. At most one action should set this — it always
   * stays visible, never collapses into the overflow menu. */
  primary?: boolean;
  /** Toggled/highlighted state for a non-primary icon button — e.g. a
   * filter button that fills in once a filter is applied. Ignored when
   * primary is set (the primary button is always filled), and ignored for
   * actions in the overflow menu (rows there don't have a filled state). */
  active?: boolean;
  loading?: boolean;
  disabled?: boolean;
  accessibilityLabel?: string;
  /** Only meaningful for the visible-secondary slot (index 0 of non-primary
   * actions — never the overflow menu, whose rows have no measurable button
   * of their own). Lets a caller that needs to position its OWN floating
   * panel (e.g. a filter dropdown with app-specific content FormHeader has
   * no generic slot for) measure this button's screen position via
   * ref.current.measureInWindow(...), instead of rendering a second,
   * separately-positioned trigger button outside FormHeader. */
  buttonRef?: React.RefObject<React.ElementRef<typeof TouchableOpacity>>;
}

export interface FormHeaderProps {
  title: string;
  subtitle?: string;
  /** Omit only for top-level tab screens (Home/Contacts/Calendar/Orders/
   * Products) reached directly from the main nav bar — every other screen
   * must pass this. When present, use useGoBack() (see below) rather than a
   * hardcoded destination, so the button follows real back-history
   * (browser Back on web, the same fallback screen on native) instead of
   * jumping to a fixed screen regardless of how the user actually got here. */
  onBack?: () => void;
  /**
   * Full logical action list — FormHeader decides what's actually visible,
   * not the caller. At most one `primary` action (always visible) plus the
   * first non-primary action stay as direct icon buttons; anything beyond
   * that collapses into a single "..." overflow button that opens a
   * dropdown of labeled rows. This keeps every screen's header the same
   * width/shape regardless of how many actions it logically has (Products'
   * ~6 actions vs. Orders' 3 vs. a form's 1 all render the same silhouette).
   */
  actions?: FormHeaderAction[];
  /** Set true ONLY when the parent ScreenLayout was given
   * disableScreenPadding — tells FormHeader not to apply its own
   * edge-to-edge compensation, since there's no parent inset to cancel out
   * in that case. Omit (the default, false) on every other screen. */
  parentScreenPaddingDisabled?: boolean;
}

/**
 * The fixed header row every screen renders above its scrollable content —
 * back button (optional, see onBack) + title (+ optional subtitle) on one
 * side, at most 2 visible action buttons + an overflow menu on the other.
 * Extracted from AddOrderScreen.tsx/AddDepartmentScreen.tsx's near-identical
 * hand-rolled versions, which is where every other screen's own ad-hoc
 * header (or complete absence of one, or a second stacked row duplicating
 * it) drifted from. Also used by top-level tab screens (Home/Contacts/
 * Calendar/Orders/Products) for the same title+actions row, omitting
 * onBack — see useGoBack() for how those screens should still wire a real
 * back button matching browser/hardware Back.
 *
 * Always render this OUTSIDE the screen's ScrollView, directly under
 * ScreenLayout's app-level `header`, as the ONLY header-ish row for the
 * screen — that's what keeps it visually fixed while the content scrolls
 * beneath it, and what keeps every screen the same height. Do not also keep
 * a screen- or child-component-owned header row (e.g. a list component's own
 * `showHeader` title bar, a breadcrumb row) stacked underneath it — fold
 * that content's actions into this component's `actions` prop instead, or
 * demote it to a plain body element below the ScrollView boundary.
 */
export const FormHeader: React.FC<FormHeaderProps> = ({ title, subtitle, onBack, actions = [], parentScreenPaddingDisabled = false }) => {
  const styles = useThemedStyles(getStyles);
  const rtl = useRTLStyles();
  const { t } = useLocale();
  useHardwareBackAction(onBack);
  // Visible title of an action: its label, else its screen-reader label, else a name for its icon.
  const titleOf = (action: FormHeaderAction) => {
    const title = action.label || action.accessibilityLabel || (ICON_TITLES[action.icon] ? t(ICON_TITLES[action.icon]!) : '');
    if (__DEV__ && !action.label && !action.accessibilityLabel && !warnedUnlabeled.has(String(action.icon)) && warnedUnlabeled.add(String(action.icon))) console.warn(`[FormHeader] Action "${String(action.icon)}" has no label — pass label so it has a title in the "More" menu.`);
    return title;
  };
  const [overflowOpen, setOverflowOpen] = useState(false);
  const [overflowAnchor, setOverflowAnchor] = useState<{ top: number; right: number } | null>(null);
  const overflowButtonRef = useRef<React.ElementRef<typeof TouchableOpacity>>(null);
  const wrapperRef = useRef<View>(null);
  // position:sticky proved unreliable across screens (nested scroll boxes,
  // stacking contexts) — position:fixed is what the app's own top nav bar
  // and RightPane already use successfully, so this measures FormHeader's
  // own natural horizontal box (left/width, which already account for
  // panes being open and the edge-to-edge negative margin) once on
  // mount/resize, then pins it to those exact viewport coordinates. The
  // wrapper below keeps the layout's normal flow height reserved (a
  // spacer) so content doesn't jump up underneath the now-fixed header.
  // `top` deliberately does NOT come from this measurement — measuring it
  // raced ScreenLayout's own app-header height settling (measured async
  // via onLayout there too), landing FormHeader partially under the app
  // header before the real height arrived. useScreenHeaderHeight() reads
  // ScreenLayout's already-settled value directly instead.
  //
  // isPinnedByLayout is true when the caller passed this FormHeader via
  // ScreenLayout's `formHeader` prop instead of rendering it inline as
  // `children` — in that mode ScreenLayout already renders it as a real
  // flex sibling of the scroll box (never nested inside it), so all of the
  // position:fixed/measurement logic below is unnecessary AND would be
  // redundant on top of layout that's already correct. Screens not yet
  // migrated to `formHeader` keep using the fixed-position fallback.
  const { headerHeight: screenHeaderHeight, isPinnedByLayout } = useScreenHeaderHeight();
  const [fixedRect, setFixedRect] = useState<{ left: number; width: number; height: number } | null>(null);
  const measure = () => {
    if (Platform.OS !== 'web' || isPinnedByLayout) return;
    wrapperRef.current?.measureInWindow((x, _y, width, height) => {
      setFixedRect({ left: x, width, height });
    });
  };
  // Re-measure whenever the left/right panes collapse/expand — their
  // Animated.Value-driven width change doesn't fire a window 'resize'
  // event, and without this the fixed header would keep the stale
  // pre-toggle width/position until the next actual window resize. The
  // pane's own slide animation is ~200ms (PANE_ANIMATION_DURATION); this
  // snaps to the final width immediately rather than animating in lockstep
  // with it, an accepted minor cosmetic gap in exchange for not duplicating
  // that Animated.Value here.
  const { leftPaneCollapsed, leftPaneWidth, rightPaneCollapsed } = useAppStore();
  const rightPaneWidth = useRightPaneWidth();
  React.useEffect(() => {
    if (Platform.OS !== 'web' || typeof window === 'undefined' || isPinnedByLayout) return;
    measure();
    const timeout = setTimeout(measure, PANE_ANIMATION_DURATION + 20);
    window.addEventListener('resize', measure);
    return () => {
      clearTimeout(timeout);
      window.removeEventListener('resize', measure);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [leftPaneCollapsed, leftPaneWidth, rightPaneCollapsed, rightPaneWidth, isPinnedByLayout]);

  const primaryAction = actions.find((a) => a.primary);
  const nonPrimary = actions.filter((a) => !a.primary);
  const visibleSecondary = nonPrimary.slice(0, 1);
  const overflowActions = nonPrimary.slice(1);

  const openOverflow = () => {
    overflowButtonRef.current?.measureInWindow((x, y, width, height) => {
      const windowWidth = Platform.OS === 'web' && typeof window !== 'undefined' ? window.innerWidth : x + width;
      // Anchor from whichever side the button is actually closer to on
      // screen — in RTL the button renders on the left (rowDirection is
      // reversed), so "right" here must be the gap from the window's LEFT
      // edge, not the button's own right-edge gap re-used on the wrong side.
      const edgeGap = rtl.isRTL ? x : windowWidth - (x + width);
      setOverflowAnchor({ top: y + height + 4, right: Math.max(spacing.md, edgeGap) });
      setOverflowOpen(true);
    });
  };

  const fixedOnWeb = Platform.OS === 'web' && !isPinnedByLayout && !!fixedRect;

  return (
    <View style={fixedOnWeb ? { height: fixedRect!.height } : undefined}>
      <View
        ref={wrapperRef}
        onLayout={fixedOnWeb || isPinnedByLayout ? undefined : measure}
        style={[
          styles.header,
          !parentScreenPaddingDisabled && styles.headerEdgeToEdge,
          fixedOnWeb && {
            position: 'fixed' as any,
            top: screenHeaderHeight,
            left: fixedRect!.left,
            width: fixedRect!.width,
            marginHorizontal: 0,
          },
        ]}
      >
      <View style={[styles.content, { flexDirection: rtl.rowDirection }]}>
        <View style={[styles.left, { flexDirection: rtl.rowDirection }]}>
          {!!onBack && (
            <TouchableOpacity style={styles.backButton} onPress={onBack} activeOpacity={0.7} hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}>
              <Ionicons name={rtl.mirrorIcon('arrow-back') as any} size={22} color={colors.textDark} />
            </TouchableOpacity>
          )}
          <View style={styles.titleGroup}>
            <Text style={[styles.title, { textAlign: rtl.textStart }]} numberOfLines={1}>{title}</Text>
            {!!subtitle && <Text style={[styles.subtitle, { textAlign: rtl.textStart }]} numberOfLines={1}>{subtitle}</Text>}
          </View>
        </View>

        {actions.length > 0 && (
          <View style={[styles.actions, { flexDirection: rtl.rowDirection }]}>
            {visibleSecondary.map((action, index) => (
              <TouchableOpacity
                key={index}
                ref={action.buttonRef}
                style={[styles.iconButton, action.active && styles.iconButtonActive, action.disabled && styles.buttonDisabled]}
                onPress={action.onPress}
                activeOpacity={0.8}
                disabled={action.disabled || action.loading}
                accessibilityLabel={action.accessibilityLabel || titleOf(action)}
                {...(Platform.OS === 'web' ? { title: titleOf(action) } as any : {})}
              >
                {action.loading ? (
                  <ActivityIndicator size="small" color={action.active ? colors.white : (colors.primary as string)} />
                ) : (
                  <Ionicons name={action.icon} size={20} color={action.active ? colors.white : colors.textDark} />
                )}
              </TouchableOpacity>
            ))}

            {overflowActions.length > 0 && (
              <TouchableOpacity
                ref={overflowButtonRef}
                style={styles.iconButton}
                onPress={openOverflow}
                activeOpacity={0.8}
                accessibilityLabel={t('common.moreActions')}
                {...(Platform.OS === 'web' ? { title: t('common.moreActions') } as any : {})}
              >
                <Ionicons name="ellipsis-horizontal" size={20} color={colors.textDark} />
              </TouchableOpacity>
            )}

            {primaryAction && (
              <TouchableOpacity
                style={[styles.primaryButton, primaryAction.disabled && styles.buttonDisabled]}
                onPress={primaryAction.onPress}
                activeOpacity={0.8}
                disabled={primaryAction.disabled || primaryAction.loading}
                accessibilityLabel={primaryAction.accessibilityLabel || titleOf(primaryAction)}
                {...(Platform.OS === 'web' ? { title: titleOf(primaryAction) } as any : {})}
              >
                {primaryAction.loading ? (
                  <ActivityIndicator size="small" color={colors.white} />
                ) : (
                  <Ionicons name={primaryAction.icon} size={22} color={colors.white} />
                )}
              </TouchableOpacity>
            )}
          </View>
        )}
      </View>

      {/* A real Modal (portal-mounted at the document root on web via
          react-native-web), NOT an inline absolute-positioned dropdown — an
          inline dropdown here shared FormHeader's own stacking context with
          the screen's scrollable content, which could paint over/clip it
          regardless of zIndex (same class of bug already fixed the same way
          in ReportsScreen.tsx's export picker). Modal always wins over the
          screen's own content, no matter what that content is. */}
      {overflowActions.length > 0 && (
        <Modal visible={overflowOpen} transparent animationType="fade" onRequestClose={() => setOverflowOpen(false)}>
          <TouchableOpacity style={styles.overflowBackdrop} activeOpacity={1} onPress={() => setOverflowOpen(false)}>
            <View
              style={[
                styles.overflowMenu,
                overflowAnchor ? { top: overflowAnchor.top, [rtl.isRTL ? 'left' : 'right']: overflowAnchor.right } : { top: 60, right: spacing.md },
              ]}
            >
              {overflowActions.map((action, index) => (
                <TouchableOpacity
                  key={index}
                  style={[styles.overflowRow, { flexDirection: rtl.rowDirection }]}
                  onPress={() => {
                    setOverflowOpen(false);
                    action.onPress();
                  }}
                  disabled={action.disabled || action.loading}
                  activeOpacity={0.7}
                >
                  {action.loading ? (
                    <ActivityIndicator size="small" color={colors.primary as string} />
                  ) : (
                    <Ionicons name={action.icon} size={18} color={colors.primary as string} />
                  )}
                  <Text style={[styles.overflowRowText, { textAlign: rtl.textStart }]} numberOfLines={1}>{titleOf(action)}</Text>
                </TouchableOpacity>
              ))}
            </View>
          </TouchableOpacity>
        </Modal>
      )}
      </View>
    </View>
  );
};

const getStyles = () => StyleSheet.create({
  header: {
    backgroundColor: colors.background.surface,
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.lg,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
    ...designTokens.shadows.sm,
    // position:sticky proved unreliable (nested scroll boxes broke it in
    // ways that didn't match the CSS spec's theory) — the component now
    // switches to position:fixed with measured coordinates once mounted
    // (see fixedRect/wrapperRef above), matching the app's own top nav bar.
    // This flexShrink/zIndex covers the brief pre-measurement instant.
    ...(Platform.OS === 'web' && { flexShrink: 0, zIndex: 999 }),
  },
  // FormHeader is meant to span the FULL screen width edge-to-edge (its own
  // paddingHorizontal above is for its CONTENT, not an inset from the
  // screen edge) — but it renders as the first child inside ScreenLayout's
  // contentArea, which applies its own paddingHorizontal:
  // spacing.screenPadding UNLESS the screen opted out via
  // disableScreenPadding. Most screens don't opt out, so without this
  // compensation FormHeader would end up double-inset (its own padding PLUS
  // the parent's), and — since different screens pass disableScreenPadding
  // inconsistently — the same header would render at a different
  // width/inset on different screens. Cancel the parent's padding here
  // whenever it's actually present (parentScreenPaddingDisabled === false)
  // so FormHeader's width never depends on that per-screen prop.
  headerEdgeToEdge: {
    marginHorizontal: -spacing.screenPadding,
  },
  content: {
    alignItems: 'center',
    justifyContent: 'space-between',
    minHeight: 40,
  },
  left: {
    flex: 1,
    alignItems: 'center',
    gap: spacing.sm,
    minWidth: 0,
  },
  backButton: {
    padding: spacing.xs,
  },
  titleGroup: {
    flex: 1,
    minWidth: 0,
  },
  title: {
    fontSize: typography.fontSize.lg,
    fontWeight: '700',
    color: colors.textDark,
    fontFamily: typography.fontFamily.bold,
  },
  subtitle: {
    fontSize: typography.fontSize.xs,
    color: colors.textLight,
    fontFamily: typography.fontFamily.regular,
    marginTop: 1,
  },
  actions: {
    alignItems: 'center',
    gap: spacing.sm,
  },
  iconButton: {
    width: 36,
    height: 36,
    borderRadius: 18,
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: colors.background.secondary,
  },
  iconButtonActive: {
    backgroundColor: colors.primary,
  },
  primaryButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: colors.primary,
    justifyContent: 'center',
    alignItems: 'center',
    ...designTokens.shadows.sm,
  },
  buttonDisabled: {
    opacity: 0.6,
  },
  overflowBackdrop: {
    flex: 1,
  },
  overflowMenu: {
    position: 'absolute',
    backgroundColor: colors.background.surfaceRaised,
    borderRadius: designTokens.borderRadius.md,
    borderWidth: 1,
    borderColor: colors.border,
    minWidth: 200,
    overflow: 'hidden',
    ...designTokens.shadows.md,
  },
  overflowRow: {
    alignItems: 'center',
    gap: spacing.sm,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    borderBottomWidth: 1,
    borderBottomColor: colors.border,
  },
  overflowRowText: {
    flex: 1,
    fontSize: typography.fontSize.sm,
    color: colors.textDark,
    fontFamily: typography.fontFamily.regular,
  },
});
